// Finance — รายการไฟล์แผนงานของแบรนด์ (Master / Media / TV Plan และอื่น ๆ)
//
// ต่างจากเมนู Documents สองข้อ:
//   1) เป็นรายการเดียวรวมทุกแคมเปญ ไม่ได้แยกกล่องต่อแคมเปญ
//   2) ไฟล์ไม่จำเป็นต้องมีแคมเปญ — อัปโหลดไว้ก่อนแล้วค่อยผูกทีหลังได้
// ไฟล์ผูกกับแบรนด์ (สินค้าเดี่ยวหรือกลุ่มสินค้า) จึงเห็นเฉพาะของแบรนด์ที่เปิดอยู่
import { useState } from "react";
import { useSearchParams } from "react-router";
import type { Brand } from "@contracts/api";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import { CampaignSelect } from "@features/campaigns/components/campaign-select";
import type { CampaignDocument, FinanceCategory } from "@features/documents/api/documents-api";
import { useDocumentMutations, useDocuments } from "@features/documents/api/use-documents";
import { PreviewModal } from "@features/documents/components/preview-modal";
import { fileKind } from "@features/documents/lib/file-kind";
import { apiErrorMessage } from "@shared/api/client";
import { FINANCE_CATEGORIES, categoryLabel } from "../lib/categories";
import { FinanceRow } from "../components/finance-row";
import { FinanceUpload } from "../components/finance-upload";

export function FinancePage({ brand }: { brand: Brand }) {
  const [params] = useSearchParams();
  const pickedCampaign = params.get("campaign") ?? "";

  const campaigns = useCampaigns(brand.id);
  const docs = useDocuments(brand.id, "finance");
  const { upload, patch, remove } = useDocumentMutations(brand.id, "finance");
  const [previewing, setPreviewing] = useState<CampaignDocument | null>(null);

  const campaignList = campaigns.data ?? [];
  const all = docs.data ?? [];
  /**
   * เลือกแคมเปญไว้ = ไฟล์ของแคมเปญนั้น **บวกไฟล์ที่ยังไม่ระบุแคมเปญ**
   *
   * ไฟล์ที่ยังไม่ระบุคือแผนระดับแบรนด์ที่ยังไม่ได้ตัดสินว่าเข้าแคมเปญไหน ซ่อนไว้แล้ว
   * จะกลายเป็นไฟล์ที่ไม่มีใครเห็นจนกว่าจะบังเอิญกลับไปเลือก "ทุกแคมเปญ" —
   * แถวของมันขึ้นว่า "ไม่ระบุแคมเปญ" อยู่แล้ว จึงไม่สับสนว่าเป็นของแคมเปญที่เลือก
   */
  const shown = pickedCampaign
    ? all.filter((d) => d.campaignId === pickedCampaign || !d.campaignId)
    : all;

  async function uploadFiles(
    files: File[],
    category: FinanceCategory,
    campaignId: string,
  ): Promise<string[]> {
    const failed: string[] = [];
    for (const file of files) {
      try {
        await upload.mutateAsync({
          productCode: brand.id,
          campaignId: campaignId || null,
          area: "finance",
          category,
          file,
          // รูปในเมนูนี้เป็นแผนที่ถ่าย/แคปมา เปิดพรีวิวไว้ให้เหมือนเมนู Documents
          preview: fileKind({ name: file.name, mime: file.type }) === "image",
        });
      } catch (e) {
        failed.push(`${file.name} — ${apiErrorMessage(e)}`);
      }
    }
    return failed;
  }

  const busyId = (id: string) =>
    (patch.isPending && patch.variables?.id === id) ||
    (remove.isPending && remove.variables === id);

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">{brand.name} / Finance</div>
          <h1>Finance</h1>
        </div>
        {!!campaignList.length && <CampaignSelect campaigns={campaignList} />}
      </div>

      <FinanceUpload campaigns={campaignList} uploading={upload.isPending} onFiles={uploadFiles} />

      {docs.isPending ? (
        <div className="panel">
          <p className="muted">กำลังโหลดเอกสาร…</p>
        </div>
      ) : docs.isError ? (
        <div className="panel empty-state">
          <h3>โหลดรายการเอกสารไม่สำเร็จ</h3>
          <p className="muted">{apiErrorMessage(docs.error)}</p>
        </div>
      ) : !shown.length ? (
        <div className="panel empty-state">
          <h3>{all.length ? "ไม่มีเอกสารของแคมเปญที่เลือก" : "ยังไม่มีเอกสาร"}</h3>
          <p className="muted">
            {all.length
              ? "แบรนด์นี้มีเอกสารอยู่ แต่ผูกกับแคมเปญอื่น — เลือก “ทุกแคมเปญ” ด้านบนเพื่อดูทั้งหมด"
              : "อัปโหลดไฟล์แผนงานได้จากกล่องด้านบน — Excel, PDF หรือรูปภาพ"}
          </p>
        </div>
      ) : (
        // จัดกลุ่มตามหมวดตามลำดับที่กำหนดไว้ หมวดที่ไม่มีไฟล์จะไม่ขึ้นเลย
        FINANCE_CATEGORIES.map((cat) => {
          const items = shown.filter((d) => (d.category ?? "other") === cat.key);
          if (!items.length) return null;
          return (
            <div className="panel fin-group" key={cat.key}>
              <div className="doc-panel-head">
                <h3>{categoryLabel(cat.key)}</h3>
                <span className="muted">{items.length} ไฟล์</span>
              </div>
              <div className="fin-list">
                {items.map((d) => (
                  <FinanceRow
                    key={d.id}
                    doc={d}
                    campaigns={campaignList}
                    busy={busyId(d.id)}
                    onOpen={() => setPreviewing(d)}
                    onCategory={(category) => patch.mutate({ id: d.id, category })}
                    onCampaign={(campaignId) => patch.mutate({ id: d.id, campaignId })}
                    onDelete={() => remove.mutate(d.id)}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}

      {previewing && <PreviewModal doc={previewing} onClose={() => setPreviewing(null)} />}
    </>
  );
}
